import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiArrowRight } from 'react-icons/fi';
import { GiBalloons, GiPartyPopper, GiCakeSlice, GiHeartWings, GiStarsStack } from 'react-icons/gi';
import { FaBaby, FaChild, FaHeart } from 'react-icons/fa';
import { collection, query, orderBy, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/client';

// Icon mapping
const iconComponents = {
  GiCakeSlice: <GiCakeSlice className="text-pink-500" size={24} />,
  GiHeartWings: <GiHeartWings className="text-pink-500" size={24} />,
  FaBaby: <FaBaby className="text-pink-500" size={24} />,
  FaChild: <FaChild className="text-pink-500" size={24} />,
  GiStarsStack: <GiStarsStack className="text-pink-500" size={24} />,
  GiPartyPopper: <GiPartyPopper className="text-pink-500" size={24} />,
  FaHeart: <FaHeart className="text-pink-500" size={24} />,
  GiBalloons: <GiBalloons className="text-pink-500" size={24} />
};

// Fallback slides in case no banners are available from Firestore
const fallbackSlides = [
  {
    id: 1,
    image: '/images/image.jpg',
    title: 'Birthday Decorations',
    subtitle: 'Transform your special day with our stunning balloon arrangements, themed setups, and personalized decorations that will make your birthday truly memorable.',
    link: '/categories',
    buttonText: 'Explore Birthday Designs',
    icon: 'GiCakeSlice'
  },
  {
    id: 2,
    image: '/images/image2.jpg',
    title: 'Anniversary Decorations',
    subtitle: 'Celebrate your love story with elegant anniversary decorations, romantic balloon setups, and custom arrangements that create the perfect atmosphere for your special milestone.',
    link: '/categories',
    buttonText: 'See Anniversary Options',
    icon: 'GiHeartWings'
  }
];

const HeroSlider = () => {
  const [slides, setSlides] = useState(fallbackSlides);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Fetch banners from Firestore
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        // First try a simple query without the composite index
        console.log('Fetching all banners...');
        const simpleBannersQuery = query(
          collection(db, 'banners')
        );
        
        const querySnapshot = await getDocs(simpleBannersQuery);
        
        if (querySnapshot.empty) {
          console.log('No banners found at all, using fallback slides');
        } else {
          // We have banners, now filter and sort them in memory
          const allBannersData = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data
            };
          });
          
          console.log(`Found ${allBannersData.length} total banners`);
          
          // Filter active banners and sort by displayOrder
          const activeBanners = allBannersData
            .filter(banner => banner.active === true)
            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
          
          console.log(`Found ${activeBanners.length} active banners`);
          
          if (activeBanners.length > 0) {
            setSlides(activeBanners);
          }
        }
      } catch (error) {
        console.error('Error fetching banners:', error);
      }
    };
    
    fetchBanners();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchBanners, 30000);
    return () => clearInterval(interval);
  }, []);

  // Helper function to get the image source
  const getImageSource = (slide) => {
    // Directly return the imageURL if it exists
    if (slide.imageURL && typeof slide.imageURL === 'string') {
      return slide.imageURL;
    }
    
    // Fallback to image property if imageURL doesn't exist
    if (slide.image) {
      return slide.image;
    }
    
    // Final fallback
    return '/images/image.jpg';
  };

  // Add a debug log when slides change
  useEffect(() => {
    console.log('Current slides:', slides.map(slide => ({
      id: slide.id,
      title: slide.title,
      hasImage: !!slide.imageURL,
      imageStart: slide.imageURL ? slide.imageURL.substring(0, 50) + '...' : 'none'
    })));
  }, [slides]);

  // Add image load error handling
  const handleImageError = (e, slide) => {
    console.error('Image failed to load:', {
      title: slide.title,
      imageURL: slide.imageURL ? `${slide.imageURL.substring(0, 100)}...` : 'none',
      fallbackUsed: e.target.src === '/images/image.jpg'
    });
    e.target.src = '/images/image.jpg'; // Fallback image
  };

  useEffect(() => {
    if (slides.length === 0) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    if (isAnimating || slides.length === 0) return;
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    if (isAnimating || slides.length === 0) return;
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative overflow-hidden h-[96vh] w-full bg-gray-800">
      {/* Elegant top border with subtle glow */}
      <div className="absolute top-0 left-0 right-0 z-40 h-1.5 bg-gradient-to-r from-pink-400 via-pink-600 to-pink-400"></div>
      
      {/* Redesigned slide counter */}
      <div className="absolute top-8 right-8 z-40 hidden md:block">
        <div className="bg-black/40 backdrop-blur-lg px-5 py-2.5 rounded-lg text-white font-semibold text-sm">
          {String(currentSlide + 1).padStart(2, '0')} <span className="text-pink-400 mx-1">/</span> {String(slides.length).padStart(2, '0')}
        </div>
      </div>
      
      <AnimatePresence initial={false} mode="sync" onExitComplete={() => setIsAnimating(false)}>
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ 
            opacity: 1,
            scale: 1,
            transition: {
              opacity: { duration: 1, ease: "easeInOut" },
              scale: { duration: 8, ease: "easeOut" }
            }
          }}
          exit={{ 
            opacity: 0,
            scale: 1.05,
            transition: { duration: 0.7, ease: "easeInOut" }
          }}
          onAnimationStart={() => setIsAnimating(true)}
          className="absolute inset-0"
        >
          {/* Background image with refined overlays */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div className="w-full h-full">
            <motion.img
                src={getImageSource(slides[currentSlide])}
                alt={slides[currentSlide].title || "Habba Events"}
                className="w-full h-full object-cover scale-[1.01]"
                style={{ 
                  objectPosition: 'center',
                  filter: 'brightness(1) contrast(1.05)'
                }}
                onLoad={() => {
                  console.log("Image loaded successfully for:", slides[currentSlide].title);
                }}
                onError={(e) => {
                  console.error("Image failed to load for:", slides[currentSlide].title);
                  e.target.src = "/images/image.jpg";
                }}
              />
            </motion.div>
            
            {/* Lighter gradient overlays to make image more visible */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-transparent"></div>
            <div className="absolute inset-0 bg-pink-600/5"></div>
            
            {/* Removed subtle spotlight effect
            <div className="absolute inset-0" style={{ 
              background: 'radial-gradient(ellipse at 25% 40%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 100%)' 
            }}></div>
            */}
          </div>
          
          {/* Content container with improved alignment and spacing */}
          <div className="relative h-full flex items-center">
            <div className="container mx-auto px-6 sm:px-8 lg:px-12">
              <motion.div 
                className="max-w-xl md:ml-12 lg:ml-20 z-20"
                initial={{ y: 60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -40, opacity: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
              >
                {/* Refined category tag */}
                <div className="inline-flex items-center px-4 py-2 rounded-lg bg-white/90 shadow-lg mb-6">
                  <span className="mr-2.5">
                    {slides[currentSlide].icon && iconComponents[slides[currentSlide].icon] ? 
                      iconComponents[slides[currentSlide].icon] : 
                      <GiCakeSlice className="text-pink-600" size={24} />
                    }
                  </span>
                  <p className="text-sm font-semibold text-pink-600">
                    Habba Events
                  </p>
                </div>
                
                {/* Title without text shadow */}
                <motion.h1 
                  className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-5 leading-[1.15]"
                  style={{ 
                    letterSpacing: '-0.02em'
                  }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {slides[currentSlide].title}
                </motion.h1>
                
                {/* Subtitle without text shadow */}
                <motion.p 
                  className="text-base md:text-lg text-white mb-7 max-w-lg leading-relaxed"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                >
                  {slides[currentSlide].subtitle}
                </motion.p>
                
                {/* Refined CTA buttons */}
                <motion.div 
                  className="flex flex-wrap gap-5 mt-7"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                >
                  <Link
                    to={slides[currentSlide].link || '/categories'}
                    className="inline-flex items-center gap-2.5 px-7 py-4 rounded-lg bg-pink-600 hover:bg-pink-700 text-white font-medium transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-pink-500/25"
                  >
                    <span>{slides[currentSlide].buttonText}</span>
                    <FiArrowRight className="ml-1 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  
                  <Link
                    to="/gallery"
                    className="inline-flex items-center px-7 py-4 rounded-lg border-2 border-white/40 text-white font-medium hover:bg-white/10 hover:border-white/60 transition-all duration-300 transform hover:-translate-y-1"
                  >
                    View Gallery
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
      
      {/* Redesigned navigation indicators */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-40 flex space-x-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
            className={`rounded-full transition-all duration-500 ${
              index === currentSlide 
                ? 'w-12 h-3 bg-pink-600' 
                : 'w-3 h-3 bg-white/30 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
      
      {/* Completely hide buttons on mobile using CSS display property */}
      <div className="absolute left-6 md:left-10 top-1/2 transform -translate-y-1/2 z-30 hidden md:block">
      <button
          className="h-14 w-14 rounded-full bg-black/30 backdrop-blur-xl border border-white/20 text-white hover:bg-pink-600 transition-all duration-300 flex items-center justify-center shadow-xl group"
        onClick={prevSlide}
        aria-label="Previous slide"
      >
          <FiChevronLeft size={28} className="group-hover:scale-125 transition-transform duration-300" />
      </button>
      </div>
      
      <div className="absolute right-6 md:right-10 top-1/2 transform -translate-y-1/2 z-30 hidden md:block">
      <button
          className="h-14 w-14 rounded-full bg-black/30 backdrop-blur-xl border border-white/20 text-white hover:bg-pink-600 transition-all duration-300 flex items-center justify-center shadow-xl group"
        onClick={nextSlide}
        aria-label="Next slide"
      >
          <FiChevronRight size={28} className="group-hover:scale-125 transition-transform duration-300" />
      </button>
      </div>
      
      {/* Subtle gradient overlays for better framing */}
      <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-black/40 to-transparent z-10 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-full h-60 bg-gradient-to-t from-black/70 to-transparent z-10 pointer-events-none"></div>
    </div>
  );
};

export default HeroSlider; 